import { useEffect, useState } from 'react';
import type { Operaciones, ParDeTokens, RetoDosPasos } from '../api/operaciones';
import type { InvitacionPublica } from '../api/ops/invitacion';
import { ErrorApi } from '../api/problema';
import { Campo } from '../componentes/Campo';
import { CodigoSeis } from '../componentes/CodigoSeis';
import { Icono } from '../componentes/Icono';
import { MENSAJES, completarDosPasos, emailValido, entrar, errorDeCampo, mensajeDeError, validarSegundoPaso } from '../logica/acceso';
import { RUTAS_ACCESO } from '../rutas';
import type { Sesion } from '../sesion/sesion';
import { tokenCaptcha } from './InvitacionCaptcha';
import { HojaRoja } from './InvitacionHoja';
import { ProtegerCuenta } from './ProtegerCuenta';
import { TEXTOS_INVITACION } from './InvitacionTextos';

/**
 * Aceptar una invitación (1.10): el enlace del correo (`/invitacion?token=`), con sesión o sin ella. El email invitado
 * (`InvitationPublic.email`, G336) va bloqueado en el alta y relleno al entrar; con la sesión abierta de otra cuenta, se
 * avisa antes de aceptar de que la invitación es para ese email.
 */
export interface PropsInvitacion {
    ops: Operaciones;
    token: string | null;
    sesion: Sesion | null;
    /** Tras entrar o crear la cuenta: guarda la sesión (sin negocio). */
    onSesion: (tokens: ParDeTokens) => void;
    /** Olvida la sesión de este navegador (es de otra cuenta: «Iniciar sesión» con la invitada). */
    onSalir?: () => void;
    /** Tras aceptar: entra en ese negocio. */
    onEntrar: (negocio: string) => Promise<void>;
    navegar: (ruta: string, reemplazar?: boolean) => void;
}


type Paso = 'cargando' | 'caducada' | 'formulario' | 'entrar' | 'dosPasos' | 'conSesion' | 'otroEmail' | 'proteger';

export function Invitacion({ ops, token, sesion, onSesion, onSalir, onEntrar, navegar }: PropsInvitacion) {
    const [paso, setPaso] = useState<Paso>(token === null || token === '' ? 'caducada' : 'cargando');
    const [invitacion, setInvitacion] = useState<InvitacionPublica | null>(null);
    const [nombre, setNombre] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [errores, setErrores] = useState<{ nombre?: string; email?: string; password?: string }>({});
    const [error, setError] = useState<string | null>(null);
    const [ocupado, setOcupado] = useState(false);
    const [reto, setReto] = useState<RetoDosPasos | null>(null);
    const [codigo, setCodigo] = useState('');
    const [recuperacion, setRecuperacion] = useState(false);
    // Con una cuenta nueva, tras aceptar se ofrece «Protege tu cuenta» (1.6) antes de entrar en el negocio.
    const [negocioAceptado, setNegocioAceptado] = useState<string | null>(null);
    // Con sesión al abrir el enlace, se acepta con esa cuenta (si es otra, `invitation_email_mismatch`).
    const [conSesionAlAbrir] = useState(sesion !== null);

    useEffect(() => {
        if (token === null || token === '') {
            return;
        }
        let vivo = true;
        ops.getInvitationByToken(token)
            .then(async (inv) => {
                // Con la sesión de otra cuenta, «Es otro email» sin esperar al 403 de acceptInvitation (si getMe falla, lo dirá él).
                const otraCuenta =
                    inv.status === 'sent' && conSesionAlAbrir
                        ? await ops.getMe().then(
                              (yo) => yo.account.email.trim().toLocaleLowerCase() !== inv.email.trim().toLocaleLowerCase(),
                              () => false,
                          )
                        : false;
                if (!vivo) {
                    return;
                }
                setInvitacion(inv);
                setEmail(inv.email);
                setPaso(inv.status !== 'sent' ? 'caducada' : otraCuenta ? 'otroEmail' : conSesionAlAbrir ? 'conSesion' : 'formulario');
            })
            .catch(() => {
                if (vivo) {
                    setPaso('caducada');
                }
            });
        return () => {
            vivo = false;
        };
    }, [ops, token, conSesionAlAbrir]);

    const ir = (p: Paso) => {
        setError(null);
        setErrores({});
        setOcupado(false);
        setPaso(p);
    };

    /**
     * acceptInvitation con la sesión ya guardada; el negocio nuevo es el de la membership con ese miembro. Con una cuenta
     * recién creada (`proteger`), antes de entrar se ofrecen los dos pasos (1.6, como el dibujo de 1.10).
     */
    const aceptar = async (proteger = false) => {
        const miembro = await ops.acceptInvitation(token ?? '');
        const yo = await ops.getMe();
        const negocio = yo.memberships.find((m) => m.member_id === miembro.id)?.business_id;
        if (negocio === undefined) {
            navegar(RUTAS_ACCESO.elegirNegocio, true);
            return;
        }
        if (proteger && !yo.account.two_factor_enabled) {
            setNegocioAceptado(negocio);
            ir('proteger');
            return;
        }
        await onEntrar(negocio);
    };

    const fallo = (e: unknown) => {
        setOcupado(false);
        if (e instanceof ErrorApi && e.codigo === 'invitation_email_mismatch') {
            ir('otroEmail');
            return;
        }
        if (e instanceof ErrorApi && e.codigo === 'email_not_verified') {
            navegar(RUTAS_ACCESO.verificarEmail, true);
            return;
        }
        const delCampo = { email: errorDeCampo(e, 'email') ?? undefined, password: errorDeCampo(e, 'password') ?? undefined, nombre: errorDeCampo(e, 'display_name') ?? undefined };
        if (delCampo.email !== undefined || delCampo.password !== undefined || delCampo.nombre !== undefined) {
            setErrores(delCampo);
            return;
        }
        setError(mensajeDeError(e, paso === 'entrar' || paso === 'dosPasos' ? (paso === 'entrar' ? 'entrar' : 'dos-pasos') : 'contrasena'));
    };

    const trabaja = (accion: () => Promise<void>) => {
        setOcupado(true);
        setError(null);
        accion().catch(fallo);
    };

    const conTokens = async (tokens: ParDeTokens, cuentaNueva = false) => {
        onSesion(tokens);
        await aceptar(cuentaNueva);
    };

    const crearCuenta = () => {
        const nuevos: typeof errores = {};
        if (nombre.trim() === '') {
            nuevos.nombre = TEXTOS_INVITACION.faltaNombre;
        }
        if (!emailValido(email)) {
            nuevos.email = MENSAJES.emailNoValido;
        }
        if (password.length < 12) {
            nuevos.password = MENSAJES.minimo12;
        }
        setErrores(nuevos);
        if (Object.keys(nuevos).length > 0) {
            return;
        }
        trabaja(async () => {
            // La cuenta nace con el email verificado si es el de la invitación (G173); después se entra y se acepta.
            await ops.createAccount({ display_name: nombre.trim(), email: email.trim(), password, invitation_token: token ?? '' }, await tokenCaptcha('alta'));
            const r = await entrar(ops, email, password);
            if (r.tipo === 'dos-pasos') {
                setReto(r.reto);
                ir('dosPasos');
                return;
            }
            await conTokens(r.tokens, true);
        });
    };

    const entrarYAceptar = () => {
        const nuevos: typeof errores = {};
        if (!emailValido(email)) {
            nuevos.email = MENSAJES.emailNoValido;
        }
        if (password === '') {
            nuevos.password = MENSAJES.faltaContrasena;
        }
        setErrores(nuevos);
        if (Object.keys(nuevos).length > 0) {
            return;
        }
        trabaja(async () => {
            const r = await entrar(ops, email, password);
            if (r.tipo === 'dos-pasos') {
                setReto(r.reto);
                setCodigo('');
                ir('dosPasos');
                return;
            }
            await conTokens(r.tokens);
        });
    };

    const segundoPaso = (valor = codigo) => {
        const mal = validarSegundoPaso(recuperacion, valor);
        if (mal !== null) {
            setError(mal);
            return;
        }
        if (reto === null) {
            return;
        }
        trabaja(async () => {
            await conTokens(await completarDosPasos(ops, reto, recuperacion, valor));
        });
    };

    const quien = invitacion?.invited_by_name ?? null;
    const cabecera = invitacion === null ? {} : { titulo: TEXTOS_INVITACION.titulo(invitacion.invited_by_name, invitacion.business_name), sub: TEXTOS_INVITACION.entrarasComo(invitacion.roles) };

    switch (paso) {
        case 'proteger':
            return (
                <ProtegerCuenta
                    ops={ops}
                    negocioDeLaInvitacion={invitacion?.business_name ?? ''}
                    onSeguir={() => {
                        if (negocioAceptado !== null) {
                            void onEntrar(negocioAceptado);
                        }
                    }}
                />
            );
        case 'cargando':
            return <HojaRoja titulo="Un momento…" />;
        case 'caducada':
            return (
                <HojaRoja
                    baldosa={['clock', 'var(--rv-paper)', 'var(--rv-ink)']}
                    titulo={TEXTOS_INVITACION.caducada}
                    sub={TEXTOS_INVITACION.caducadaSub(quien)}
                    enlaces={[{ antes: TEXTOS_INVITACION.yaFormasParte, texto: 'Iniciar sesión', accion: () => { navegar(RUTAS_ACCESO.entrar); } }]}
                />
            );
        case 'otroEmail':
            return (
                <HojaRoja
                    volver={{ texto: 'Volver', accion: () => { ir(sesion === null ? 'formulario' : 'conSesion'); } }}
                    baldosa={['mail-x', 'var(--rv-red)', '#fff']}
                    titulo={TEXTOS_INVITACION.otroEmail}
                    sub={MENSAJES.invitacionOtroEmail}
                    principal={{
                        texto: 'Iniciar sesión',
                        icono: 'log-in',
                        ocupado: 'Un momento…',
                        accion: () => {
                            // La sesión abierta es de otra cuenta: se cierra y se entra con la invitada.
                            if (sesion !== null) {
                                void ops.deleteCurrentSession().catch(() => undefined);
                                onSalir?.();
                            }
                            setEmail(invitacion?.email ?? '');
                            setPassword('');
                            ir('entrar');
                        },
                    }}
                    enlaces={[{ texto: TEXTOS_INVITACION.volverALaInvitacion, accion: () => { ir(sesion === null ? 'formulario' : 'conSesion'); } }]}
                >
                    {invitacion !== null && (
                        <div className="rv-acceso__nota">
                            <Icono nombre="mail" />
                            <span>{TEXTOS_INVITACION.paraEmail(invitacion.email)}</span>
                        </div>
                    )}
                </HojaRoja>
            );
        case 'conSesion':
            return (
                <HojaRoja
                    {...cabecera}
                    error={error}
                    ocupado={ocupado}
                    principal={{ texto: TEXTOS_INVITACION.aceptar, icono: 'arrow-right', ocupado: 'Aceptando…', accion: () => { trabaja(aceptar); } }}
                />
            );
        case 'dosPasos':
            return (
                <HojaRoja
                    volver={{ texto: 'Volver', accion: () => { ir('entrar'); } }}
                    titulo={recuperacion ? 'Código de recuperación' : 'Verificación en dos pasos'}
                    sub={recuperacion ? 'Uno de los que guardaste. Cada uno sirve una vez.' : 'Escribe los 6 números de tu app de autenticación.'}
                    error={error}
                    ocupado={ocupado}
                    principal={{ texto: 'Verificar', icono: 'shield-check', ocupado: 'Comprobando…', accion: () => { segundoPaso(); } }}
                    enlaces={[
                        {
                            texto: recuperacion ? 'Usar la aplicación' : 'Usar un código de recuperación',
                            accion: () => {
                                setRecuperacion((r) => !r);
                                setCodigo('');
                                setError(null);
                            },
                        },
                    ]}
                >
                    <div className="rv-acceso__campo">
                        {recuperacion ? (
                            <Campo etiqueta="Código de recuperación" autocompletar="one-time-code" mayusculas="none" valor={codigo} onChange={setCodigo} onEnter={() => { segundoPaso(); }} />
                        ) : (
                            <CodigoSeis valor={codigo} onChange={setCodigo} error={error !== null} autoFocus onCompleto={segundoPaso} />
                        )}
                    </div>
                </HojaRoja>
            );
        case 'entrar':
            return (
                <HojaRoja
                    {...cabecera}
                    volver={{ texto: 'Volver', accion: () => { ir('formulario'); } }}
                    error={error}
                    ocupado={ocupado}
                    principal={{ texto: TEXTOS_INVITACION.entrarYAceptar, icono: 'log-in', ocupado: 'Entrando…', accion: entrarYAceptar }}
                >
                    <div className="rv-acceso__campos">
                        <div className="rv-acceso__campo">
                            <Campo etiqueta="Email" tipo="email" autocompletar="username" valor={email} error={errores.email} onChange={setEmail} onEnter={entrarYAceptar} />
                        </div>
                        <div className="rv-acceso__campo">
                            <Campo etiqueta="Contraseña" tipo="password" autocompletar="current-password" valor={password} error={errores.password} onChange={setPassword} onEnter={entrarYAceptar} />
                        </div>
                    </div>
                </HojaRoja>
            );
        case 'formulario':
            return (
                <HojaRoja
                    {...cabecera}
                    error={error}
                    ocupado={ocupado}
                    principal={{ texto: TEXTOS_INVITACION.aceptar, icono: 'arrow-right', ocupado: 'Creando tu cuenta…', accion: crearCuenta }}
                    enlaces={[{ antes: TEXTOS_INVITACION.yaTienesCuenta, texto: TEXTOS_INVITACION.entrarYAceptar, accion: () => { ir('entrar'); } }]}
                    pie={
                        <>
                            {/* Enlaces de las condiciones y la privacidad de Reservaria: pendientes de su dirección y versión (G188). */}
                            <p className="rv-invitacion__legal">
                                Al crear la cuenta aceptas las <u>condiciones del servicio</u> y la <u>política de privacidad</u>.
                            </p>
                            <p className="rv-invitacion__legal">
                                Este sitio está protegido por reCAPTCHA y se aplican la{' '}
                                <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer">
                                    Política de privacidad
                                </a>{' '}
                                y las{' '}
                                <a href="https://policies.google.com/terms" target="_blank" rel="noreferrer">
                                    Condiciones del servicio
                                </a>{' '}
                                de Google.
                            </p>
                        </>
                    }
                >
                    <div className="rv-acceso__campos">
                        <div className="rv-acceso__campo">
                            <Campo etiqueta="Nombre y apellidos" autocompletar="name" valor={nombre} error={errores.nombre} onChange={setNombre} />
                        </div>
                        {/* El email invitado (G336), bloqueado como en el diseño: la cuenta nace verificada con él (G173). */}
                        <div className="rv-acceso__campo">
                            <Campo etiqueta="Email" tipo="email" autocompletar="email" valor={email} error={errores.email} onChange={setEmail} bloqueado={invitacion !== null} />
                        </div>
                        <div className="rv-acceso__campo">
                            <Campo etiqueta="Contraseña" tipo="password" autocompletar="new-password" valor={password} error={errores.password} onChange={setPassword} onEnter={crearCuenta} />
                        </div>
                    </div>
                    <div className="rv-acceso__nota">
                        <Icono nombre="mail-check" />
                        <span>{TEXTOS_INVITACION.sinVerificar}</span>
                    </div>
                </HojaRoja>
            );
    }
}
